import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FlaskConical, Loader2, Upload, X, ChevronDown, FileDown } from "lucide-react";
import { useBrand } from "@/contexts/BrandContext";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { WorkflowTestComparison, TestRun, TestImageResult } from "./WorkflowTestComparison";

export const MAX_TEST_IMAGES = 5;
const BUCKET = "device-test-images";

/** @deprecated kept for existing imports; results are now shown inside this section. */
export interface TestResultData {
  result: string;
  reason: string;
  message: string;
  subject: string;
  data: any[];
  imageUrl: string;
}

interface WorkflowTestSectionProps {
  deviceId: string;
  device: any;
  disabled?: boolean;
  onTestResult?: (result: TestResultData) => void;
}

interface TestImage { path: string; url: string; }

export const WorkflowTestSection = ({ deviceId, device, disabled }: WorkflowTestSectionProps) => {
  const [images, setImages] = useState<TestImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [runs, setRuns] = useState<TestRun[]>([]);
  const [open, setOpen] = useState(false);
  const hasAutoExpandedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [aoiNoticeOpen, setAoiNoticeOpen] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const { brand, isWhiteLabel } = useBrand();
  const { user } = useAuth();
  const latestRun = runs[0];
  const latestDone = !!latestRun && latestRun.results.every((r) => r.status !== "loading");

  const handleReport = async () => {
    if (!latestRun) return;
    setIsReporting(true);
    try {
      const { buildWorkflowTestReport } = await import("./workflowTestReport");
      const brandName = isWhiteLabel ? brand?.config?.company_name || "Snapshot Live" : "Snapshot Live";
      const logoUrl = (isWhiteLabel && brand?.config?.logo_url) || "/lovable-uploads/37267525-cc1e-42cd-a617-eb98609d4050.png";
      const doc = await buildWorkflowTestReport({ device, deviceId, run: latestRun, logoUrl, brandName, tintLogo: !(isWhiteLabel && brand?.config?.logo_url), generatedBy: user?.email ?? null });
      const name = String(device?.device_name || device?.name || deviceId).replace(/[^a-z0-9-_]+/gi, "_");
      doc.save(`${name}_ai-test-report_${latestRun.startedAt.toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      toast.error(`Could not create the report: ${e instanceof Error ? e.message : "unknown error"}`);
    } finally {
      setIsReporting(false);
    }
  };
  const hasAoi = Array.isArray(device?.boundary_boxes) && device.boundary_boxes.length > 0;

  const model: string | null = device?.notification_ai_model || device?.notificationAiModel || null;

  const loadImages = useCallback(async () => {
    const { data } = await supabase.storage.from(BUCKET).list(deviceId, { sortBy: { column: "created_at", order: "asc" } });
    const list = (data ?? [])
      .filter((f) => f.name && !f.name.startsWith("."))
      .map((f) => {
        const path = `${deviceId}/${f.name}`;
        return { path, url: supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl };
      });
    setImages(list);
    if (list.length > 0 && !hasAutoExpandedRef.current) {
      hasAutoExpandedRef.current = true;
      setOpen(true);
    }
  }, [deviceId]);

  useEffect(() => { loadImages(); }, [loadImages]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const room = MAX_TEST_IMAGES - images.length;
    if (files.length > room) toast.error(`You can have up to ${MAX_TEST_IMAGES} test images`);
    const toUpload = files.slice(0, Math.max(room, 0));

    setIsUploading(true);
    setOpen(true);
    try {
      let lastUrl: string | null = null;
      for (const file of toUpload) {
        if (!file.type.startsWith("image/")) { toast.error(`${file.name} is not an image`); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} must be under 10MB`); continue; }
        const ext = file.name.split(".").pop() || "jpg";
        const path = `${deviceId}/test-image-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
        const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true });
        if (error) throw error;
        lastUrl = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
      }
      if (lastUrl) {
        await supabase.from("device_details").update({ test_image_url: lastUrl }).eq("device_id", deviceId);
        toast.success("Test images uploaded");
      }
      await loadImages();
    } catch (error) {
      toast.error(`Upload failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = async (img: TestImage) => {
    const { error } = await supabase.storage.from(BUCKET).remove([img.path]);
    if (error) { toast.error(`Could not remove image: ${error.message}`); return; }
    setImages((prev) => prev.filter((i) => i.path !== img.path));
  };

  const handleTestAll = async () => {
    if (!images.length) return;
    setOpen(true);
    const runId = `${Date.now()}`;
    const initial: TestImageResult[] = images.map((img) => ({ imageUrl: img.url, status: "loading" }));
    setRuns((prev) => [{ id: runId, model, startedAt: new Date(), results: initial }, ...prev].slice(0, 6));
    setIsTesting(true);

    const update = (idx: number, patch: Partial<TestImageResult>) =>
      setRuns((prev) => prev.map((r) => r.id !== runId ? r : {
        ...r, results: r.results.map((res, i) => (i === idx ? { ...res, ...patch } : res)),
      }));

    await Promise.all(images.map(async (img, idx) => {
      try {
        const { data, error } = await supabase.functions.invoke("ai-workflow-test", {
          body: { deviceId, imageUrl: img.url },
        });
        if (error) {
          let msg = error.message;
          try { const body = await (error as any).context?.json?.(); if (body?.error) msg = typeof body.error === "string" ? body.error : JSON.stringify(body.error); } catch { /* ignore */ }
          throw new Error(msg);
        }
        let parsedData: Record<string, number> = {};
        try { parsedData = data?.data ? JSON.parse(data.data) : {}; } catch { parsedData = {}; }
        update(idx, {
          status: data?.status === "ok" ? "done" : data?.status === "ai_disabled" ? "disabled" : "error",
          result: data?.result, reason: data?.reason, message: data?.message, subject: data?.subject,
          data: parsedData, model: data?.model ?? model, durationMs: data?.durationMs,
        });
      } catch (err) {
        update(idx, { status: "error", reason: err instanceof Error ? err.message : "Unknown error", subject: "Test failed" });
      }
    }));
    setIsTesting(false);
  };

  return (
    <Card className="border bg-muted/50">
      <AlertDialog open={aoiNoticeOpen} onOpenChange={setAoiNoticeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Areas of Interest won't be drawn</AlertDialogTitle>
            <AlertDialogDescription>
              This test only checks the AI query. Your saved Areas of Interest will not be drawn onto the test images. Please make sure your uploaded images already show these areas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setAoiNoticeOpen(false); void handleTestAll(); }}>Run test</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Collapsible open={open} onOpenChange={setOpen}>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2">
          <CollapsibleTrigger asChild>
            <button type="button" className="flex items-center gap-2 cursor-pointer select-none">
              <FlaskConical className="h-4 w-4 text-brand-500" />
              <CardTitle className="text-base">Test &amp; Validation</CardTitle>
              <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
            </button>
          </CollapsibleTrigger>
          <div className="flex items-center gap-2">
          {latestRun && (
            <Button variant="outline" size="sm" className="h-8" onClick={handleReport} disabled={isReporting || isTesting || !latestDone}>
              {isReporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileDown className="h-4 w-4 mr-1" />}
              {isReporting ? "Creating..." : "Download Report"}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => (hasAoi ? setAoiNoticeOpen(true) : handleTestAll())}
            disabled={disabled || isTesting || !images.length || !model}
            className="h-8"
            style={{ backgroundColor: "#DFEF97", color: "black" }}
          >
            {isTesting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FlaskConical className="h-4 w-4 mr-1" />}
            {isTesting ? "Testing..." : images.length > 1 ? `Test all ${images.length} images` : "Test Workflow"}
          </Button>
          </div>
        </div>
        {!model && (
          <p className="text-xs text-muted-foreground mt-1">Enable AI and choose a model above to run a test.</p>
        )}
      </CardHeader>
      <CollapsibleContent>
      <CardContent className="py-3 space-y-3">
        <p className="text-xs text-muted-foreground">
          Upload up to {MAX_TEST_IMAGES} test images. Each test uses the saved question, data tags and the selected AI model.
        </p>

        <div className="flex gap-2 flex-wrap">
          {images.map((img, i) => (
            <div key={img.path} className="relative rounded-md overflow-hidden border">
              <img src={img.url} alt={`Test image ${i + 1}`} className="h-24 w-36 object-cover" />
              <span className="absolute bottom-1 left-1 bg-primary text-primary-foreground text-[10px] font-medium px-1.5 py-0.5 rounded">
                {i + 1}
              </span>
              <button
                type="button"
                onClick={() => removeImage(img)}
                disabled={isTesting}
                aria-label={`Remove test image ${i + 1}`}
                className="absolute top-1 right-1 rounded-full bg-background/90 p-0.5 border hover:bg-accent"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {!images.length && <p className="text-xs text-muted-foreground italic py-4">No test images yet.</p>}
        </div>

        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || images.length >= MAX_TEST_IMAGES}
            className="h-8 text-xs"
          >
            {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
            {isUploading ? "Uploading..." : "Upload test images"}
          </Button>
          <span className="text-[11px] text-muted-foreground">{images.length}/{MAX_TEST_IMAGES}</span>
        </div>

        {runs.length > 0 && <WorkflowTestComparison runs={runs} onClear={() => setRuns([])} />}
      </CardContent>
      </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
